import axios from 'axios';

const getBaseUrl = () => {
  if (typeof window !== 'undefined' && window.location) {
    return process.env.REACT_APP_API_URL || `${window.location.origin}/api`;
  }
  return process.env.REACT_APP_API_URL || '/api';
};

const axiosInstance = axios.create({
  baseURL: getBaseUrl(),
});

// ============ SESSION EXPIRY INTERCEPTOR ============
// Intercepts 401 responses globally — clears auth and redirects to home page
const handleAuthError = (error) => {
  if (error.response && error.response.status === 401) {
    const url = error.config?.url || '';
    // Skip login and registration credential failures (should show inline error)
    if (url.includes('/auth/login') || url.includes('/auth/register')) {
      return Promise.reject(error);
    }
    // Clear stored auth data whenever token expires or is rejected on protected routes
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    delete axiosInstance.defaults.headers.common['Authorization'];

    // Redirect to home page with session expired flag (avoid redirect loops)
    if (
      typeof window !== 'undefined' &&
      window.location.pathname !== '/' &&
      !window.location.pathname.startsWith('/login')
    ) {
      window.location.href = '/?sessionExpired=true';
    }
  }
  return Promise.reject(error);
};

axiosInstance.interceptors.response.use((response) => response, handleAuthError);
axios.interceptors.response.use((response) => response, handleAuthError);

// Helper to add auth headers
export const authHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
});

// Helper to set global auth token
export const setAuthToken = (token) => {
  if (token) {
    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axiosInstance.defaults.headers.common['Authorization'];
  }
};

// ============ AUTHENTICATION APIs ============

export const registerUser = async (data) => {
  const res = await axiosInstance.post('/auth/register', data);
  return res.data;
};

export const loginUser = async (email, password) => {
  const res = await axiosInstance.post('/auth/login', { email, password });
  return res.data;
};

// ============ FILE UPLOAD APIs ============

export const uploadPaper = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await axiosInstance.post('/upload/paper', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return res.data;
};

// ============ USER PROFILE APIs ============

/**
 * Get current user profile
 * @returns {Promise} User profile data
 */
export const getUserProfile = async () => {
  const res = await axiosInstance.get('/auth/me');
  return res.data.data || res.data;
};

/**
 * Update user profile
 * @param {Object} data - Updated profile data
 * @returns {Promise} Updated user data
 */
export const updateUserProfile = async (data) => {
  const res = await axiosInstance.put('/auth/profile', data);
  return res.data.data || res.data;
};

// ============ ACCOUNT MIGRATION APIs ============

export const sendMigrationOtp = async (email) => {
  const res = await axiosInstance.post('/auth/migrate/send-otp', { email });
  return res.data;
};

export const completeEmailMigration = async (data) => {
  const res = await axiosInstance.post('/auth/migrate/set-email-password', data);
  return res.data;
};

export const linkGoogleAccount = async (code) => {
  const res = await axiosInstance.post('/auth/migrate/link-google', { code });
  return res.data;
};

// ============ TRACK APIs ============

export const getTracks = async (conferenceId) => {
  try {
    const token = localStorage.getItem('token');
    let role = null;
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        role = payload.role;
      } catch (e) {
        // ignore parse errors
      }
    }

    let res;
    if (role === 'organizer') {
      res = await axiosInstance.get(`/tracks/conference/${conferenceId}`);
    } else if (role === 'reviewer') {
      res = await axiosInstance.get(`/reviewer/conferences/${conferenceId}/tracks`);
    } else {
      // Author and others
      res = await axiosInstance.get(`/author/conferences/${conferenceId}/tracks`);
    }
    return res.data;
  } catch (err) {
    console.error('Error fetching tracks:', err);
    return { data: [] };
  }
};

export const createTrack = async (conferenceId, trackData) => {
  const res = await axiosInstance.post('/tracks', {
    conferenceId,
    ...trackData,
  });
  return res.data;
};

export const updateTrack = async (trackId, updates) => {
  const res = await axiosInstance.put(`/tracks/${trackId}`, updates);
  return res.data;
};

export const deleteTrack = async (trackId) => {
  const res = await axiosInstance.delete(`/tracks/${trackId}`);
  return res.data;
};

// ============ AUTHOR APIs ============

export const getAuthorDashboard = async () => {
  const res = await axiosInstance.get('/author/dashboard');
  return res.data;
};

export const discoverConferences = async (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });
  const res = await axiosInstance.get(`/author/conferences?${params}`);
  return res.data;
};

export const getConferenceDetailsAuthor = async (conferenceId) => {
  const res = await axiosInstance.get(`/author/conferences/${conferenceId}`);
  return res.data;
};

export const submitPaper = async (conferenceId, submissionData) => {
  const res = await axiosInstance.post(
    `/author/conferences/${conferenceId}/submissions`,
    submissionData
  );
  return res.data;
};

export const getAuthorSubmissions = async (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });
  const res = await axiosInstance.get(`/author/submissions?${params}`);
  return res.data;
};

export const getSubmissionDetailsAuthor = async (submissionId) => {
  const res = await axiosInstance.get(`/author/submissions/${submissionId}`);
  return res.data;
};

export const uploadRevision = async (submissionId, revisionData) => {
  const res = await axiosInstance.put(
    `/author/submissions/${submissionId}/revision`,
    revisionData
  );
  return res.data;
};

// ============ REVIEWER APIs ============

export const getReviewerDashboard = async () => {
  const bids = await getReviewerBids();
  const reviews = await getReviewerReviews();
  return { data: { bids, reviews } };
};

export const placeBid = async (submissionId, confidence) => {
  const res = await axiosInstance.post('/reviewer/bids', {
    submissionId,
    ...(confidence && { confidence }),
  });
  return res.data;
};

export const getReviewerBids = async (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });
  const res = await axiosInstance.get(`/reviewer/bids?${params}`);
  return res.data;
};

export const withdrawBid = async (bidId) => {
  const res = await axiosInstance.delete(`/reviewer/bids/${bidId}`);
  return res.data;
};

export const getReviewerAssignments = async () => {
  const res = await axiosInstance.get('/reviewer/assignments');
  return res.data;
};

export const createReview = async (submissionId, reviewData) => {
  const res = await axiosInstance.post(
    `/reviewer/submissions/${submissionId}/reviews`,
    reviewData
  );
  return res.data;
};

export const getSubmissionForReview = async (submissionId) => {
  const res = await axiosInstance.get(`/reviewer/submissions/${submissionId}`);
  return res.data;
};

export const getReviewerReviews = async (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });
  const res = await axiosInstance.get(`/reviewer/reviews?${params}`);
  return res.data;
};

export const getReviewerRegistrations = async () => {
  const res = await axiosInstance.get('/reviewer/registrations');
  return res.data;
};

export const registerReviewerConference = async (conferenceId) => {
  const res = await axiosInstance.post('/reviewer/registrations', { conferenceId });
  return res.data;
};

export const getSubmissionReviewsReviewer = async (submissionId, filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });
  const res = await axiosInstance.get(
    `/reviewer/submissions/${submissionId}/reviews?${params}`
  );
  return res.data;
};

export const getReviewerMyReview = async (submissionId) => {
  const res = await axiosInstance.get(`/reviewer/submissions/${submissionId}/my-review`);
  return res.data;
};

export const getConferencesForReviewer = async (filters = {}) => {
  return discoverConferences(filters);
};

export const getConferenceSubmissionsReviewer = async (conferenceId, filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });
  const res = await axiosInstance.get(
    `/reviewer/conferences/${conferenceId}/submissions?${params}`
  );
  return res.data;
};

// ============ REVIEWER CONFERENCE REGISTRATION APIs ============

export const registerReviewerForConference = async (conferenceId) => {
  const res = await axiosInstance.post(`/reviewer/conferences/${conferenceId}/register`);
  return res.data;
};

export const getReviewerConferenceRegistrationStatus = async (conferenceId) => {
  const res = await axiosInstance.get(`/reviewer/conferences/${conferenceId}/registration-status`);
  return res.data;
};

export const getReviewerConferenceRegistrations = async () => {
  const res = await axiosInstance.get('/reviewer/registrations');
  return res.data;
};

// ============ ORGANIZER APIs ============

export const getOrganizerDashboard = async () => {
  const res = await axiosInstance.get('/organizer/conferences');
  return res.data;
};

export const getConferenceDetailsOrganizer = async (conferenceId) => {
  const res = await axiosInstance.get(`/organizer/conferences/${conferenceId}`);
  return res.data;
};

export const createConference = async (conferenceData) => {
  const res = await axiosInstance.post('/organizer/conferences', conferenceData);
  return res.data;
};

export const updateConference = async (conferenceId, updateData) => {
  const res = await axiosInstance.put(`/organizer/conferences/${conferenceId}`, updateData);
  return res.data;
};

export const getConferenceSubmissionsOrganizer = async (conferenceId, filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });
  const res = await axiosInstance.get(
    `/organizer/conferences/${conferenceId}/submissions?${params}`
  );
  return res.data;
};

export const makeSubmissionDecision = async (submissionId, decision, feedback) => {
  const res = await axiosInstance.patch(
    `/organizer/submission/${submissionId}/decision`,
    { decision, feedback }
  );
  return res.data;
};

export const approveSubmission = async (submissionId) => {
  const res = await axiosInstance.put(`/organizer/submissions/${submissionId}/approve`);
  return res.data;
};

export const bulkApproveSubmissions = async (submissionIds) => {
  const res = await axiosInstance.post(`/organizer/submissions/bulk-approve`, { submissionIds });
  return res.data;
};

export const updateSubmissionStatus = async (submissionId, status, feedback) => {
  const res = await axiosInstance.put(`/organizer/submissions/${submissionId}/status`, {
    status,
    feedback,
  });
  return res.data;
};

export const scheduleSubmission = async (submissionId, scheduleData) => {
  const res = await axiosInstance.put(
    `/organizer/submissions/${submissionId}/schedule`,
    scheduleData
  );
  return res.data;
};

export const getSubmissionDetailsOrganizer = async (submissionId) => {
  const res = await axiosInstance.get(`/organizer/submissions/${submissionId}`);
  return res.data;
};

export const getSubmissionReviewsOrganizer = async (submissionId, filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });
  const res = await axiosInstance.get(
    `/organizer/submissions/${submissionId}/reviews?${params}`
  );
  return res.data;
};

export const getOrganizerReviews = async (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });
  const res = await axiosInstance.get(`/organizer/reviews?${params}`);
  return res.data;
};

export const getConferenceReviews = async (conferenceId, filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });
  const res = await axiosInstance.get(
    `/organizer/conferences/${conferenceId}/reviews?${params}`
  );
  return res.data;
};

export const generateCertificates = async (conferenceId) => {
  const res = await axiosInstance.post(`/organizer/conferences/${conferenceId}/certificates`);
  return res.data;
};

export const markAttendance = async (registrationId, attended) => {
  const res = await axiosInstance.put(`/organizer/registrations/${registrationId}/attendance`, {
    attended,
  });
  return res.data;
};

export const getConferenceParticipants = async (conferenceId) => {
  const res = await axiosInstance.get(`/organizer/conferences/${conferenceId}/participants`);
  return res.data;
};

// ============ ORGANIZER BID MANAGEMENT APIs ============

export const getOrganizerBids = async (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });
  const res = await axiosInstance.get(`/organizer/bids?${params}`);
  return res.data;
};

export const getConferenceBids = async (conferenceId, filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });
  const res = await axiosInstance.get(`/organizer/conferences/${conferenceId}/bids?${params}`);
  return res.data;
};

export const updateBidStatus = async (bidId, status, reason = '') => {
  const res = await axiosInstance.patch(`/organizer/bids/${bidId}`, { status, reason });
  return res.data;
};

export const bulkUpdateBids = async (bidIds, status, reason = '') => {
  const res = await axiosInstance.post('/organizer/bids/bulk-update', { bidIds, status, reason });
  return res.data;
};

// ============ ORGANIZER ASSIGNMENT APIs ============

export const getConferenceAssignments = async (conferenceId, filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });
  const res = await axiosInstance.get(`/organizer/conferences/${conferenceId}/assignments?${params}`);
  return res.data;
};

export const runAutoAssign = async (conferenceId, config = {}) => {
  const res = await axiosInstance.post(`/organizer/conferences/${conferenceId}/auto-assign`, config);
  return res.data;
};

export const createManualAssignment = async (reviewerId, submissionId, notes = '') => {
  const res = await axiosInstance.post('/organizer/assignments', { reviewerId, submissionId, notes });
  return res.data;
};

export const updateAssignment = async (assignmentId, updates) => {
  const res = await axiosInstance.put(`/organizer/assignments/${assignmentId}`, updates);
  return res.data;
};

export const deleteAssignment = async (assignmentId) => {
  const res = await axiosInstance.delete(`/organizer/assignments/${assignmentId}`);
  return res.data;
};

export const getAssignmentAnalytics = async (conferenceId) => {
  const res = await axiosInstance.get(`/organizer/conferences/${conferenceId}/assignment-analytics`);
  return res.data;
};

export const getEligibleReviewers = async (conferenceId) => {
  const res = await axiosInstance.get(`/organizer/conferences/${conferenceId}/eligible-reviewers`);
  return res.data;
};

// ============ DUPLICATION CHECK APIs ============

export const retryDuplicationCheck = async (submissionId) => {
  const res = await axiosInstance.post(`/organizer/submissions/${submissionId}/retry-dup-check`);
  return res.data;
};

export const getAuthorDuplicatePapers = async () => {
  const res = await axiosInstance.get('/author/duplicate-papers');
  return res.data;
};

export const getConferenceDuplicatePapers = async (conferenceId) => {
  const res = await axiosInstance.get(`/organizer/conferences/${conferenceId}/duplicate-papers`);
  return res.data;
};

// ============ PARTICIPANT APIs ============

export const getParticipantDashboard = async () => {
  const res = await axiosInstance.get('/participant/dashboard');
  return res.data;
};

export const getConferencesParticipant = async (filters = {}) => {
  return discoverConferences(filters);
};

export const getConferenceDetailsParticipant = async (conferenceId) => {
  const res = await axiosInstance.get(`/participant/conferences/${conferenceId}`);
  return res.data;
};

export const registerForConference = async (conferenceId) => {
  const res = await axiosInstance.post('/participant/registrations', { conferenceId });
  return res.data;
};

export const getParticipantRegistrations = async () => {
  const res = await axiosInstance.get('/participant/registrations');
  return res.data;
};

export const getParticipantCertificates = async () => {
  const res = await axiosInstance.get('/participant/certificates');
  return res.data;
};

export const downloadParticipantCertificate = async (certificateId) => {
  const res = await axiosInstance.get(`/participant/certificates/${certificateId}/download`, {
    responseType: 'blob',
  });
  return res.data;
};

// ============ AUTHOR CERTIFICATE APIs ============

export const getAuthorCertificates = async () => {
  const res = await axiosInstance.get('/author/certificates');
  return res.data;
};

export const downloadAuthorCertificate = async (certificateId) => {
  const res = await axiosInstance.get(`/author/certificates/${certificateId}/download`, {
    responseType: 'blob',
  });
  return res.data;
};

// ============ REVIEWER CERTIFICATE APIs ============

export const getReviewerCertificates = async () => {
  const res = await axiosInstance.get('/reviewer/certificates');
  return res.data;
};

export const downloadReviewerCertificate = async (certificateId) => {
  const res = await axiosInstance.get(`/reviewer/certificates/${certificateId}/download`, {
    responseType: 'blob',
  });
  return res.data;
};

// ============ ORGANIZER CERTIFICATE APIs ============

export const markAuthorAttendance = async (submissionId, attended) => {
  const res = await axiosInstance.put(`/organizer/submissions/${submissionId}/attendance`, {
    attended,
  });
  return res.data;
};

export const getConferenceAuthors = async (conferenceId) => {
  const res = await axiosInstance.get(`/organizer/conferences/${conferenceId}/authors`);
  return res.data;
};

export const getCertificateStats = async (conferenceId) => {
  const res = await axiosInstance.get(`/organizer/conferences/${conferenceId}/certificate-stats`);
  return res.data;
};

export const uploadGeneralChairSignature = async (conferenceId, file) => {
  const formData = new FormData();
  formData.append('signature', file);
  const res = await axiosInstance.post(
    `/organizer/conferences/${conferenceId}/signature`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return res.data;
};

// ============ ORGANIZER REVIEWER APPROVAL APIs ============

export const getOrganizerReviewerRegistrations = async (conferenceId, filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.append(key, value);
  });
  const res = await axiosInstance.get(
    `/organizer/conferences/${conferenceId}/reviewer-registrations?${params}`
  );
  return res.data;
};

export const getOrganizerReviewerRegistrationDetails = async (conferenceId, regId) => {
  const res = await axiosInstance.get(
    `/organizer/conferences/${conferenceId}/reviewer-registrations/${regId}`
  );
  return res.data;
};

export const approveReviewerRegistration = async (conferenceId, regId, data = {}) => {
  const res = await axiosInstance.patch(
    `/organizer/conferences/${conferenceId}/reviewer-registrations/${regId}/approve`,
    data
  );
  return res.data;
};

export const rejectReviewerRegistration = async (conferenceId, regId, data = {}) => {
  const res = await axiosInstance.patch(
    `/organizer/conferences/${conferenceId}/reviewer-registrations/${regId}/reject`,
    data
  );
  return res.data;
};

export const bulkApproveReviewerRegistrations = async (conferenceId, regIds, reason = '') => {
  const res = await axiosInstance.post(
    `/organizer/conferences/${conferenceId}/reviewer-registrations/bulk-approve`,
    { regIds, reason }
  );
  return res.data;
};

export const bulkRejectReviewerRegistrations = async (conferenceId, regIds, reason = '', reviewerReason = '') => {
  const res = await axiosInstance.post(
    `/organizer/conferences/${conferenceId}/reviewer-registrations/bulk-reject`,
    { regIds, reason, reviewerReason }
  );
  return res.data;
};

// ============ CONFERENCE RUBRICS APIs ============

export const uploadConferenceRubrics = async (conferenceId, file, trackIds = 'ALL') => {
  const formData = new FormData();
  formData.append('file', file);
  if (Array.isArray(trackIds)) {
    formData.append('trackIds', JSON.stringify(trackIds));
  } else {
    formData.append('trackIds', trackIds);
  }
  const res = await axiosInstance.post(
    `/organizer/conferences/${conferenceId}/rubrics`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return res.data;
};

export const deleteTrackRubric = async (conferenceId, trackId) => {
  const res = await axiosInstance.delete(`/organizer/conferences/${conferenceId}/tracks/${trackId}/rubric`);
  return res.data;
};

export const deleteAllConferenceRubrics = async (conferenceId) => {
  const res = await axiosInstance.delete(`/organizer/conferences/${conferenceId}/rubrics/all`);
  return res.data;
};

// ============ DEFAULT EXPORT (for legacy support) ============

export default axiosInstance;


export const checkEmailRegistered = async (email) => {
  const response = await axiosInstance.get(`/author/check-email?email=${encodeURIComponent(email)}`);
  return response.data;
};
